import React from 'react';
import { Check, Layers, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

/**
 * Custom Membership Tiers tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 2).
 * Pure presentational component — all state and handlers stay owned by the
 * parent so this drop-in extraction is behaviourally identical.
 */
export default function CustomTiersTab({
  customTiers,
  showTierForm,
  setShowTierForm,
  newTier,
  setNewTier,
  tierFeaturesInput,
  setTierFeaturesInput,
  handleCreateTier,
  handleDeleteTier,
}) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-playfair font-bold text-foreground flex items-center gap-2">
          <Layers className="w-6 h-6 text-purple-500" /> Custom Membership Tiers
        </h2>
        <Button
          onClick={() => setShowTierForm(!showTierForm)}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-custom-tier-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Custom Tier
        </Button>
      </div>
      <p className="text-muted-foreground">
        Create additional membership tiers beyond Ascend, Growth, and Founder. Custom tiers will automatically integrate with the Millionaire Pot calculations.
      </p>

      {showTierForm && (
        <div className="wm-card p-6 rounded-lg border-2 border-purple-500/30">
          <h3 className="text-lg font-semibold text-foreground mb-4">Create New Tier</h3>
          <form onSubmit={handleCreateTier} className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-foreground">Tier Name *</Label>
              <Input
                value={newTier.name}
                onChange={(e) => setNewTier({ ...newTier, name: e.target.value })}
                placeholder="e.g., Diamond"
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div>
              <Label className="text-foreground">Slug (URL-friendly) *</Label>
              <Input
                value={newTier.slug}
                onChange={(e) => setNewTier({ ...newTier, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                placeholder="e.g., diamond"
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div>
              <Label className="text-foreground">Monthly Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={newTier.price}
                onChange={(e) => setNewTier({ ...newTier, price: parseFloat(e.target.value) || 0 })}
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div>
              <Label className="text-foreground">Initiation Fee ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={newTier.initiation_fee}
                onChange={(e) => setNewTier({ ...newTier, initiation_fee: parseFloat(e.target.value) || 0 })}
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div>
              <Label className="text-foreground">Tier Level (1=lowest access)</Label>
              <Input
                type="number"
                value={newTier.tier_level}
                onChange={(e) => setNewTier({ ...newTier, tier_level: parseInt(e.target.value) || 4 })}
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div>
              <Label className="text-foreground">Listings Allocation</Label>
              <Input
                type="number"
                value={newTier.listings_allocation}
                onChange={(e) => setNewTier({ ...newTier, listings_allocation: parseInt(e.target.value) || 0 })}
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-foreground">Features (one per line)</Label>
              <Textarea
                value={tierFeaturesInput}
                onChange={(e) => setTierFeaturesInput(e.target.value)}
                placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                rows={4}
                className="mt-1 bg-input border-border text-foreground"
              />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={newTier.can_recruit}
                  onChange={(e) => setNewTier({ ...newTier, can_recruit: e.target.checked })}
                  className="rounded border-border"
                />
                Can Recruit Members
              </label>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  checked={newTier.can_create_courses}
                  onChange={(e) => setNewTier({ ...newTier, can_create_courses: e.target.checked })}
                  className="rounded border-border"
                />
                Can Create Courses
              </label>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                Create Tier
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowTierForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Existing Custom Tiers */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {customTiers.map(tier => (
          <div key={tier.id} className="wm-card p-5 rounded-lg border border-purple-500/30" data-testid="custom-tier-card">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-lg font-semibold text-purple-400">{tier.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteTier(tier.id)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-2xl font-bold text-foreground mb-2">${tier.price}<span className="text-sm text-muted-foreground">/mo</span></p>
            {tier.initiation_fee > 0 && (
              <p className="text-sm text-muted-foreground mb-2">+ ${tier.initiation_fee} initiation</p>
            )}
            <p className="text-sm text-muted-foreground mb-2">Level: {tier.tier_level} • {tier.listings_allocation.toLocaleString()} listings</p>
            {tier.features && tier.features.length > 0 && (
              <ul className="text-xs text-muted-foreground space-y-1">
                {tier.features.slice(0, 3).map((f, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <Check className="w-3 h-3 text-purple-400 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
                {tier.features.length > 3 && <li className="text-purple-400">+{tier.features.length - 3} more</li>}
              </ul>
            )}
          </div>
        ))}
        {customTiers.length === 0 && (
          <div className="md:col-span-3 text-center py-12 wm-card rounded-lg">
            <Layers className="w-12 h-12 text-purple-500/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No custom tiers created yet</p>
          </div>
        )}
      </div>
    </div>
  );
}
